import React, { useEffect, useRef, useState } from 'react';
import uuid from 'uuid-random';
import { ButtonVariant, ButtonColor, Tooltip } from 'react-magma-dom';
import {
  StyledDropdown,
  StyledDropdownContent,
  StyledDropdownMenuItem,
  StyledToolButton,
  ToolButtonWrapper,
} from '../Styled';
import { DropdownOption } from '../../types';
import { undoRedoShortcutsHandler } from '../../utils';
import { TOOLS } from '../../constants/edit';

interface IEditToolButton {
  ariaLabel: string;
  onClick: (e: MouseEvent | KeyboardEvent) => void;
  icon: JSX.Element;
  isHighlighted: boolean;
  isDisabled?: boolean;
  withDisabledStyle?: boolean;
  bgColor: string;
  testId?: string;
  showTooltip?: boolean;
  tooltipContent?: string | JSX.Element;
  isDropdown?: boolean;
  dropdownOptions?: DropdownOption[];
}

const defaultTooltipStyles = {};

export const ToolButton: React.FC<IEditToolButton> = (props): JSX.Element => {
  const {
    ariaLabel,
    onClick,
    icon,
    isHighlighted,
    isDisabled,
    withDisabledStyle,
    bgColor,
    testId,
    showTooltip,
    tooltipContent,
    isDropdown,
    dropdownOptions,
  } = props;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isUndo = testId === TOOLS.undo.name;
  const isRedo = testId === TOOLS.redo.name;

  const getTooltipStyle = () => {
    return {
      ...defaultTooltipStyles,
    };
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((isUndo || isRedo) && !isDisabled) {
        undoRedoShortcutsHandler(e, { isUndo, isRedo }, onClick);
      }
    };

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }
    };

    if ((isUndo || isRedo) && !isDisabled) {
      document.addEventListener('keydown', handleKeyDown);
    }

    if (isDropdown) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      if ((isUndo || isRedo) && !isDisabled) {
        document.removeEventListener('keydown', handleKeyDown);
      }
      if (isDropdown) {
        document.removeEventListener('mousedown', handleOutsideClick);
      }
    };
  }, [isUndo, isRedo, isDisabled, isDropdown, onClick]);

  const button = (
    <ToolButtonWrapper disabled={isDisabled} isHighlighted={isHighlighted || isDropdownOpen} bgColor={bgColor}>
      <StyledToolButton
        aria-label={ariaLabel}
        disabled={isDisabled}
        withDisabledStyle={withDisabledStyle}
        onClick={isDropdown ? toggleDropdown : onClick}
        icon={icon}
        variant={ButtonVariant.link}
        color={ButtonColor.danger}
        testId={testId || 'tool-button'}
      />
    </ToolButtonWrapper>
  );

  const renderButton = () => {
    if (showTooltip && !isDropdownOpen) {
      return (
        <Tooltip key={uuid()} isInverse tooltipStyle={getTooltipStyle()} content={tooltipContent}>
          {button}
        </Tooltip>
      );
    }

    return button;
  };

  if (isDropdown) {
    return (
      <StyledDropdown ref={dropdownRef}>
        {renderButton()}
        {isDropdownOpen && (
          <StyledDropdownContent>
            {dropdownOptions?.map(({ name, callback }) => (
              <StyledDropdownMenuItem onClick={callback} key={name} data-testid="dropdown-item">
                {name}
              </StyledDropdownMenuItem>
            ))}
          </StyledDropdownContent>
        )}
      </StyledDropdown>
    );
  }

  return renderButton();
};
